import { useEffect, useRef } from 'react';
import { Modal } from 'lib/mortar/components/Modal';
import dynamic from 'next/dynamic';
import Funnelling from 'components/Funnelling/Funnelling';
import type { Skill } from 'lib/sdks/search';
import { FlowId, updateFlow, clearFlow, stopFlow } from 'lib/sdks/analytics';

import useFunnelling from './hooks/useFunnelling';
import { DirectMessageFunnelFlowSteps } from 'lib/sdks/analytics/types';
import { useSelector } from 'react-redux';
import type { UseFunnelNextCallback } from './types';
import TopLevelCategorySelect from './screens/TopLevelCategorySelectScreen';
import { JOB_TYPE } from 'lib/sdks/jobs-management/job.constants';
import {
  getPreferredStartField,
  type PreferredStartOption,
} from './utils/preferredStart';

import LocationScreen from './screens/LocationScreen';
import { getTradeRadius } from './utils/getTradeRadius';
import { isFullPostcode } from '@utils/postcode-validation';
import { selectRecentSearchLocation } from 'containers/AppContainer/selectors';
import { getTrackingPropsFromFunnelling } from './utils/tracking';
import { JobStartScreen } from './screens/JobStartScreen';
import { useJob } from './job.context';
import { useGetInitialCategory } from './hooks/useGetInitialCategory';
import type { MessageJourneyProps } from './MessageJourney';
import { updateFlowContext } from 'lib/sdks/analytics/flow';
import { createFlowScreen, useFlow } from './useFlow';
import ProgressLoader from 'components/Loader/ProgressLoader';
import { DeadEndAltTrades } from './screens/dead-end-alt-trades';
import { useGetABTestVariant } from '@hooks/use-ab-test';
import {
  START_DATE_SCREEN,
  StartDateScreenStates,
} from '@redux/ab-tests.constants';
import { FeedbackModal } from './components/FeedbackModal';
import { useDisplayFeedbackModal } from '@hooks/use-display-feedback-modal';

const MessageJourney = dynamic<MessageJourneyProps>(
  () => import('./MessageJourney'),
);

const SCREEN = {
  categorySelect: 'category-select',
  filtering: 'filtering',
  location: 'location',
  outsideTradeRadius: 'outside-trade-radius',
  noCategory: 'no-category',
  message: 'message',
  jobStartDate: 'job-start-date',
} as const;

type ScreenKey = (typeof SCREEN)[keyof typeof SCREEN];

const mapStepToTrackingId = {
  [SCREEN.categorySelect]: DirectMessageFunnelFlowSteps.CHOOSE_PRIMARY_CATEGORY,
  [SCREEN.location]: DirectMessageFunnelFlowSteps.LOCATION_SCREEN,
  [SCREEN.noCategory]: DirectMessageFunnelFlowSteps.NO_CATEGORY,
  [SCREEN.outsideTradeRadius]:
    DirectMessageFunnelFlowSteps.OUTSIDE_TRADE_RADIUS,
  [SCREEN.message]: DirectMessageFunnelFlowSteps.SEND_A_MESSAGE,
} as const;

type RAQFunnelProps = {
  from: 'search' | 'profile' | 'alternative-trades';
  serviceId?: string;
  serviceVersionId?: string;
  jobRefinementEnabled?: boolean;
  name?: string;
  skills: Skill[];
  categoryId?: number;
  companyId?: number;
  searchLocation?: string;
  uniqueName?: string;
  onDismiss: () => void;
  forceStartDateFirst?: boolean;
  disableAnimation?: boolean;
  preferredStart?: PreferredStartOption;
};

const RAQFunnel = ({
  from,
  serviceId,
  serviceVersionId,
  jobRefinementEnabled,
  name,
  skills,
  categoryId,
  companyId,
  onDismiss,
  uniqueName,
  searchLocation = '',
  forceStartDateFirst,
  disableAnimation,
  preferredStart,
}: RAQFunnelProps) => {
  const { job, setJobField } = useJob();

  const flowInitialised = useRef<boolean>(false);
  const initialCategoryId = useGetInitialCategory(skills, categoryId);
  const recentSearchLocation = useSelector(selectRecentSearchLocation) || '';
  const isSearchLocationValid = isFullPostcode(searchLocation);

  const { displayFeedbackModal, feedbackCallback, exitWithFeedbackCheck } =
    useDisplayFeedbackModal();

  const { steps, currentStep, setStep, goToPreviousStep } = useFlow<ScreenKey>({
    flowId: FlowId.DIRECT_MESSAGE_FUNNELLING,
    trackingStepMap: mapStepToTrackingId,
  });
  const funnelling = useFunnelling();

  const handleMainCategorySelect = async ({
    value: selectedCategoryId,
  }: {
    value: number;
  }) => {
    setJobField('categoryId', selectedCategoryId);

    const [isFunnelling, config] = await funnelling.initialise(
      selectedCategoryId,
      {
        type: 'Filtering',
      },
    );
    updateFlowContext(FlowId.DIRECT_MESSAGE_FUNNELLING, {
      type: job.type,
      company_id: companyId,
      ...getTrackingPropsFromFunnelling({
        categoryId: selectedCategoryId,
        funnelConfig: config,
        fieldValues: {},
      }),
    });

    if (isFunnelling) {
      flowInitialised.current = true;

      updateFlow(FlowId.DIRECT_MESSAGE_FUNNELLING, config.startingScreen);
      setStep(SCREEN.filtering);
      return;
    }

    if (!isSearchLocationValid && job.type !== JOB_TYPE.requestAQuote) {
      setStep(SCREEN.location);
      return;
    }

    if (isSearchLocationValid) {
      setJobField('userProfile', { address: { postcode: searchLocation } });
    }

    if (flowInitialised.current) {
      clearFlow(
        FlowId.DIRECT_MESSAGE_FUNNELLING,
        undefined,
        DirectMessageFunnelFlowSteps.SEND_A_MESSAGE,
      );
    }

    setStep(SCREEN.message, { sendTrackEvent: false });
  };

  const abStartDateScreenFirst =
    useGetABTestVariant(START_DATE_SCREEN) ===
    StartDateScreenStates.START_DATE_SCREEN_FIRST;

  useEffect(() => {
    const getInitialScreen = () => {
      // If preferredStart is provided, set it and skip the start date screen
      if (preferredStart) {
        const preferredStartField = getPreferredStartField(preferredStart);
        if (preferredStartField) {
          setJobField('preferredStart', preferredStartField);
        }
      }

      if (forceStartDateFirst || abStartDateScreenFirst) {
        return setStep(SCREEN.jobStartDate);
      }

      if (!initialCategoryId) {
        flowInitialised.current = true;
        updateFlowContext(FlowId.DIRECT_MESSAGE_FUNNELLING, {
          type: job.type,
          company_id: companyId,
        });
        return setStep(SCREEN.categorySelect);
      }

      // Set serviceId and serviceVersionId if available
      if (serviceId) {
        setJobField('serviceId', serviceId);
      }

      if (serviceVersionId) {
        setJobField('serviceVersionId', serviceVersionId);
      }

      handleMainCategorySelect({ value: initialCategoryId });
    };

    getInitialScreen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCategoryId, preferredStart]);

  const tradeRadiusCheck = async (
    postcode: string,
    selectedCategory: number,
  ) =>
    job.type === JOB_TYPE.requestAQuote
      ? true
      : await getTradeRadius(selectedCategory, postcode, uniqueName as string);

  const handleLocationSubmit = async (postcode: string) => {
    const selectedCategory = job.categoryId as number;

    const isInTradeWorkRadius = await tradeRadiusCheck(
      postcode,
      selectedCategory,
    );

    setJobField('userProfile', { address: { postcode } });

    updateFlowContext(FlowId.DIRECT_MESSAGE_FUNNELLING, {
      category_id: selectedCategory,
      postcode,
    });

    if (!isInTradeWorkRadius) {
      setStep(SCREEN.outsideTradeRadius);
      return;
    }

    setStep(SCREEN.message, { cleared: true });
  };

  const handleCompletedFunnel = async ({
    categoryId: selectedCategoryId,
    postcode,
  }: {
    categoryId: number | null;
    postcode: string;
  }) => {
    if (selectedCategoryId === null) {
      return;
    }

    const isInTradeWorkRadius = await tradeRadiusCheck(
      postcode,
      selectedCategoryId,
    );
    setJobField('categoryId', selectedCategoryId);
    setJobField('userProfile', { address: { postcode } });

    updateFlowContext(FlowId.DIRECT_MESSAGE_FUNNELLING, {
      category_id: selectedCategoryId,
      postcode,
    });

    const hasSkill = skills.find(
      skill =>
        skill.id === selectedCategoryId ||
        skill.subSkills.find(subSkill => subSkill.id === selectedCategoryId),
    );

    if (!isInTradeWorkRadius && hasSkill) {
      setStep(SCREEN.outsideTradeRadius);
      return;
    }

    if (hasSkill || job.type === JOB_TYPE.requestAQuote) {
      setStep(SCREEN.message, { cleared: true });
      return;
    }

    setStep(SCREEN.noCategory);
  };

  const handleFunnellingSelection: UseFunnelNextCallback = (
    screen,
    choice,
    nextScreen,
  ) => {
    updateFlowContext(FlowId.DIRECT_MESSAGE_FUNNELLING, {
      question: {
        ...funnelling.fieldValues,
        [screen]: choice,
      },
    });
    updateFlow(
      FlowId.DIRECT_MESSAGE_FUNNELLING,
      nextScreen || DirectMessageFunnelFlowSteps.LOCATION_SCREEN,
    );
  };

  const completeJobStart = () => {
    handleMainCategorySelect({ value: job.categoryId || initialCategoryId });
  };

  const handleMessageCompany = () => {
    setStep(SCREEN.message, { sendTrackEvent: false });
  };

  /**
   * Used to exit at the end of flow
   */
  const closeModal = () => {
    funnelling.stop();
    onDismiss();
  };

  /**
   * Used to exit during the flow
   */
  const stopAndClose = () => {
    stopFlow(FlowId.DIRECT_MESSAGE_FUNNELLING);
    closeModal();
  };

  /**
   * Goes back to previous screen, or exits if no previous screen
   */
  const onBack = () => {
    goToPreviousStep();

    if (steps.length > 1) {
      return;
    }

    exitWithFeedbackCheck(stopAndClose);
  };

  const screens = {
    [SCREEN.jobStartDate]: createFlowScreen(JobStartScreen, {
      onComplete: completeJobStart,
    }),
    [SCREEN.message]: createFlowScreen(MessageJourney, {
      from,
      companyId,
      companyName: name as string,
      jobRefinementEnabled,
      serviceId,
      serviceVersionId,
    }),
    [SCREEN.noCategory]: createFlowScreen(DeadEndAltTrades, {
      heading: `${name} may not operate in this area or offer this service`,
      onMessageCompany: handleMessageCompany,
      subheading: `Here are some tradespeople that offer this service in ${job?.userProfile?.address?.postcode?.toUpperCase()}`,
      icon: 'info',
      excludeCompany: companyId,
    }),
    [SCREEN.outsideTradeRadius]: createFlowScreen(DeadEndAltTrades, {
      heading: `${name} may not operate in this area`,
      onMessageCompany: handleMessageCompany,
      subheading: `Here are some tradespeople that operate in ${job?.userProfile?.address?.postcode?.toUpperCase()}`,
      icon: 'locationPin',
      excludeCompany: companyId,
    }),
    [SCREEN.categorySelect]: createFlowScreen(TopLevelCategorySelect, {
      onComplete: handleMainCategorySelect,
      skills,
    }),
    [SCREEN.filtering]: createFlowScreen(Funnelling, {
      onComplete: handleCompletedFunnel,
      onSelect: handleFunnellingSelection,
      locationButtonText: 'Continue',
      ...funnelling,
    }),
    [SCREEN.location]: createFlowScreen(LocationScreen, {
      buttonText: 'Continue',
      onComplete: handleLocationSubmit,
      value: recentSearchLocation,
    }),
  } as const;

  const step = screens[currentStep];
  const ScreenComponent = step?.component;
  const screenProps = step?.props as any;

  const style = {
    minHeight: '65vh',
  };

  if (currentStep === SCREEN.message) {
    // @ts-ignore
    style.paddingBottom = '0';
  }

  const isRequestAQuote = job.type === JOB_TYPE.requestAQuote;

  if (feedbackCallback && displayFeedbackModal) {
    return <FeedbackModal stopAndClose={feedbackCallback} />;
  }

  return (
    <Modal
      name="Request A Quote"
      size="medium"
      fullScreenOnMobile
      style={style}
      onDismiss={() => {
        exitWithFeedbackCheck(stopAndClose);
      }}
      disableAnimation={disableAnimation}
    >
      {step ? (
        <ScreenComponent
          {...screenProps}
          isRequestAQuote={isRequestAQuote}
          onDismiss={() => exitWithFeedbackCheck(closeModal)}
          onBack={onBack}
        />
      ) : (
        <ProgressLoader />
      )}
    </Modal>
  );
};

export default RAQFunnel;
